import { useState, useMemo } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import {
    ArrowLeft,
    Trophy,
    AlertCircle,
    CheckCircle2,
    XCircle,
    BookOpen,
    Eye,
    EyeOff,
    HelpCircle,
    ChevronLeft,
    ChevronRight,
    Search,
    Sparkles,
    RotateCcw
} from 'lucide-react';
import { QUIZ_CATEGORIES, QUIZ_PASS_MARK, shuffleAndSlice, type QuizQuestion } from '../../../data/quizCategories';

export default function TakeQuiz() {
    const { categoryId } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    
    // Find category by ID or alias
    const category = QUIZ_CATEGORIES.find(
        (c) => c.id === categoryId || (c.aliases && c.aliases.includes(categoryId || ''))
    );

    const initialMode = searchParams.get('mode') === 'study' ? 'study' : 'quiz';
    const [activeTab, setActiveTab] = useState<'quiz' | 'study'>(initialMode);

    // Quiz Mode States
    const [quizQuestionCount, setQuizQuestionCount] = useState<number>(10);
    const [quizStarted, setQuizStarted] = useState(false);
    const [sessionQuestions, setSessionQuestions] = useState<QuizQuestion[]>([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [selectedAnswers, setSelectedAnswers] = useState<Record<number, number>>({});
    const [result, setResult] = useState<{ score: number; passed: boolean; correctCount: number } | null>(null);
    const [showReview, setShowReview] = useState(false);

    // Study Mode States
    const [studyIndex, setStudyIndex] = useState(0);
    const [showStudyAnswer, setShowStudyAnswer] = useState(false);
    const [studySearch, setStudySearch] = useState('');

    if (!category) {
        return (
            <div className="max-w-xl mx-auto px-4 py-24 text-center space-y-4">
                <AlertCircle className="mx-auto text-red-500" size={40} />
                <h2 className="text-xl font-bold">Module Not Found</h2>
                <p className="text-[var(--index-text-secondary)] text-sm font-medium">
                    The requested quiz module could not be found.
                </p>
                <Link
                    to="/student/quiz"
                    className="inline-flex items-center gap-2 text-[var(--index-primary-color)] font-bold text-sm no-underline hover:underline"
                >
                    <ArrowLeft size={16} /> Back to General Quiz
                </Link>
            </div>
        );
    }

    const Icon = category.icon;

    // Filter study questions by search
    const filteredStudyQuestions = useMemo(() => {
        if (!studySearch.trim()) return category.questions;
        const q = studySearch.toLowerCase();
        return category.questions.filter(
            (item) =>
                item.question.toLowerCase().includes(q) ||
                item.rawAnswer.toLowerCase().includes(q) ||
                `q${item.qNum}`.includes(q)
        );
    }, [category.questions, studySearch]);

    // Handle Quiz Start
    const handleStartQuiz = () => {
        const selectedCount = quizQuestionCount === 100 ? category.questions.length : quizQuestionCount;
        setSessionQuestions(shuffleAndSlice(category.questions, selectedCount));
        setCurrentIndex(0);
        setSelectedAnswers({});
        setResult(null);
        setShowReview(false);
        setQuizStarted(true);
    };

    // Handle Quiz Submit
    const handleSubmitQuiz = () => {
        let correct = 0;
        sessionQuestions.forEach((q, idx) => {
            const userPick = selectedAnswers[idx] !== undefined ? Number(selectedAnswers[idx]) : -1;
            const rightAns = Number(q.correctAnswer);
            if (userPick === rightAns) correct++;
        });
        const score = Math.round((correct / sessionQuestions.length) * 100);
        setResult({ score, passed: score >= QUIZ_PASS_MARK, correctCount: correct });
    };

    const currentQuizQuestion = sessionQuestions[currentIndex];
    const hasSelectedCurrent = selectedAnswers[currentIndex] !== undefined;
    const answeredCount = Object.keys(selectedAnswers).length;

    // Handle Tab Switch
    const switchTab = (tab: 'quiz' | 'study') => {
        setActiveTab(tab);
        setSearchParams({ mode: tab });
    };

    return (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-8 pb-16 text-[var(--index-text-heading)]">
            {/* Top Navigation Bar */}
            <div className="flex items-center justify-between py-6 border-b border-[var(--index-border-color)]/60 mb-6">
                <Link
                    to="/student/quiz"
                    className="inline-flex items-center gap-2 text-[var(--index-text-secondary)] hover:text-[var(--index-primary-color)] font-bold text-xs uppercase tracking-wider no-underline transition-colors"
                >
                    <ArrowLeft size={16} /> Back to Modules
                </Link>

                <div className="flex items-center gap-2">
                    <button
                        onClick={() => switchTab('quiz')}
                        className={`px-3.5 py-1.5 rounded-xl text-xs font-bold uppercase tracking-wider transition-all flex items-center gap-1.5 ${
                            activeTab === 'quiz'
                                ? 'bg-[var(--index-primary-color)] text-white shadow-sm'
                                : 'bg-[var(--index-hover-bg)] text-[var(--index-text-secondary)] hover:text-[var(--index-text-heading)]'
                        }`}
                    >
                        <HelpCircle size={14} /> Diagnostic Quiz
                    </button>
                    <button
                        onClick={() => switchTab('study')}
                        className={`px-3.5 py-1.5 rounded-xl text-xs font-bold uppercase tracking-wider transition-all flex items-center gap-1.5 ${
                            activeTab === 'study'
                                ? 'bg-[var(--index-primary-color)] text-white shadow-sm'
                                : 'bg-[var(--index-hover-bg)] text-[var(--index-text-secondary)] hover:text-[var(--index-text-heading)]'
                        }`}
                    >
                        <BookOpen size={14} /> Study Bank (100 Q&A)
                    </button>
                </div>
            </div>

            {/* ========================================================= */}
            {/* STUDY / FLASHCARD MODE */}
            {/* ========================================================= */}
            {activeTab === 'study' && (
                <div className="space-y-6">
                    {/* Study Header */}
                    <div className="bg-[var(--index-card-bg)] border border-[var(--index-border-color)] rounded-[28px] p-6 md:p-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                            <div
                                className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0"
                                style={{ backgroundColor: category.color + '15' }}
                            >
                                <Icon size={28} color={category.color} />
                            </div>
                            <div>
                                <div className="text-[10px] font-bold uppercase tracking-wider text-[var(--index-text-secondary)]">
                                    {category.project}
                                </div>
                                <h1 className="text-xl md:text-2xl font-black tracking-tight">{category.title}</h1>
                                <p className="text-xs text-[var(--index-text-secondary)] font-medium">
                                    Browse and master all 100 curriculum questions & verified answers.
                                </p>
                            </div>
                        </div>

                        {/* Search in Study Bank */}
                        <div className="relative w-full md:w-64">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--index-text-secondary)]" size={15} />
                            <input
                                type="text"
                                value={studySearch}
                                onChange={(e) => {
                                    setStudySearch(e.target.value);
                                    setStudyIndex(0);
                                    setShowStudyAnswer(false);
                                }}
                                placeholder="Search this module..."
                                className="w-full pl-9 pr-3 py-2 rounded-xl bg-[var(--index-hover-bg)] border border-[var(--index-border-color)] text-xs font-medium text-[var(--index-text-heading)] focus:outline-none focus:border-[var(--index-primary-color)]"
                            />
                        </div>
                    </div>

                    {filteredStudyQuestions.length > 0 ? (
                        <>
                            {/* Flashcard Card */}
                            {(() => {
                                const currentItem = filteredStudyQuestions[studyIndex] || filteredStudyQuestions[0];
                                return (
                                    <div className="bg-[var(--index-card-bg)] border border-[var(--index-border-color)] rounded-[32px] p-6 md:p-10 space-y-6 shadow-sm relative overflow-hidden">
                                        {/* Card Top bar */}
                                        <div className="flex items-center justify-between border-b border-[var(--index-border-color)]/60 pb-4">
                                            <span className="text-xs font-black uppercase tracking-wider text-[var(--index-primary-color)] flex items-center gap-1.5">
                                                <Sparkles size={14} /> Question {currentItem.qNum} of 100
                                            </span>
                                            <span className="text-xs font-bold text-[var(--index-text-secondary)]">
                                                Card {studyIndex + 1} / {filteredStudyQuestions.length}
                                            </span>
                                        </div>

                                        {/* Question Text */}
                                        <div className="space-y-3">
                                            <div className="text-[10px] font-black uppercase tracking-wider text-[var(--index-text-secondary)]">
                                                Question:
                                            </div>
                                            <h2 className="text-lg md:text-2xl font-black leading-snug tracking-tight text-[var(--index-text-heading)]">
                                                {currentItem.question}
                                            </h2>
                                        </div>

                                        {/* Answer Section */}
                                        <div className="pt-2">
                                            {showStudyAnswer ? (
                                                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-2xl p-5 md:p-6 space-y-3 animate-fadeIn">
                                                    <div className="flex items-center justify-between">
                                                        <span className="text-xs font-black uppercase tracking-wider text-emerald-600 dark:text-emerald-400 flex items-center gap-1.5">
                                                            <CheckCircle2 size={15} /> Verified Answer & Key Concept
                                                        </span>
                                                        <button
                                                            onClick={() => setShowStudyAnswer(false)}
                                                            className="text-xs font-bold text-[var(--index-text-secondary)] hover:text-[var(--index-text-heading)] flex items-center gap-1"
                                                        >
                                                            <EyeOff size={13} /> Hide
                                                        </button>
                                                    </div>
                                                    <p className="text-sm md:text-base font-semibold leading-relaxed text-emerald-950 dark:text-emerald-100">
                                                        {currentItem.rawAnswer}
                                                    </p>
                                                </div>
                                            ) : (
                                                <button
                                                    onClick={() => setShowStudyAnswer(true)}
                                                    className="w-full py-4 rounded-2xl border-2 border-dashed border-[var(--index-border-color)] hover:border-[var(--index-primary-color)]/60 bg-[var(--index-hover-bg)]/40 hover:bg-[var(--index-hover-bg)] transition-all flex items-center justify-center gap-2 font-bold text-xs uppercase tracking-wider text-[var(--index-text-secondary)] hover:text-[var(--index-primary-color)]"
                                                >
                                                    <Eye size={16} /> Click to Reveal Answer
                                                </button>
                                            )}
                                        </div>

                                        {/* Navigation Controls */}
                                        <div className="flex items-center justify-between pt-4 border-t border-[var(--index-border-color)]/60">
                                            <button
                                                onClick={() => {
                                                    setStudyIndex((prev) => Math.max(0, prev - 1));
                                                    setShowStudyAnswer(false);
                                                }}
                                                disabled={studyIndex === 0}
                                                className="px-5 py-2.5 rounded-xl font-bold text-xs uppercase tracking-wider border border-[var(--index-border-color)] text-[var(--index-text-secondary)] disabled:opacity-30 hover:bg-[var(--index-hover-bg)] transition-all flex items-center gap-1"
                                            >
                                                <ChevronLeft size={16} /> Previous
                                            </button>

                                            <div className="flex items-center gap-1 overflow-x-auto max-w-[200px] sm:max-w-sm px-2">
                                                {filteredStudyQuestions.slice(Math.max(0, studyIndex - 2), studyIndex + 3).map((item, idx) => {
                                                    const actualIdx = filteredStudyQuestions.indexOf(item);
                                                    return (
                                                        <button
                                                            key={actualIdx}
                                                            onClick={() => {
                                                                setStudyIndex(actualIdx);
                                                                setShowStudyAnswer(false);
                                                            }}
                                                            className={`w-8 h-8 rounded-lg font-black text-xs transition-all ${
                                                                actualIdx === studyIndex
                                                                    ? 'bg-[var(--index-primary-color)] text-white'
                                                                    : 'bg-[var(--index-hover-bg)] text-[var(--index-text-secondary)] hover:text-[var(--index-text-heading)]'
                                                            }`}
                                                        >
                                                            {item.qNum}
                                                        </button>
                                                    );
                                                })}
                                            </div>

                                            <button
                                                onClick={() => {
                                                    setStudyIndex((prev) => Math.min(filteredStudyQuestions.length - 1, prev + 1));
                                                    setShowStudyAnswer(false);
                                                }}
                                                disabled={studyIndex === filteredStudyQuestions.length - 1}
                                                className="px-6 py-2.5 rounded-xl font-bold text-xs uppercase tracking-wider text-white transition-all flex items-center gap-1 disabled:opacity-30"
                                                style={{ backgroundColor: category.color }}
                                            >
                                                Next <ChevronRight size={16} />
                                            </button>
                                        </div>
                                    </div>
                                );
                            })()}
                        </>
                    ) : (
                        <div className="text-center py-16 bg-[var(--index-card-bg)] border border-[var(--index-border-color)] rounded-[32px] space-y-3">
                            <Search className="mx-auto text-[var(--index-text-secondary)]" size={32} />
                            <p className="font-bold text-base">No questions match "{studySearch}"</p>
                            <button
                                onClick={() => setStudySearch('')}
                                className="text-xs font-bold text-[var(--index-primary-color)] uppercase tracking-wider hover:underline"
                            >
                                Reset Search
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* ========================================================= */}
            {/* DIAGNOSTIC QUIZ MODE */}
            {/* ========================================================= */}
            {activeTab === 'quiz' && (
                <>
                    {!quizStarted ? (
                        /* Pre-quiz Setup Screen */
                        <div className="bg-[var(--index-card-bg)] border border-[var(--index-border-color)] rounded-[32px] p-8 md:p-12 text-center space-y-8 shadow-sm">
                            <div
                                className="w-20 h-20 rounded-3xl flex items-center justify-center mx-auto"
                                style={{ backgroundColor: category.color + '15' }}
                            >
                                <Icon size={42} color={category.color} />
                            </div>

                            <div className="space-y-2 max-w-xl mx-auto">
                                <div className="text-xs font-bold uppercase tracking-wider text-[var(--index-text-secondary)]">
                                    {category.project}
                                </div>
                                <h1 className="text-2xl md:text-3xl font-black tracking-tight">{category.title}</h1>
                                <p className="text-[var(--index-text-secondary)] text-sm md:text-base font-medium leading-relaxed">
                                    {category.description}
                                </p>
                            </div>

                            {/* Length Selector */}
                            <div className="space-y-3 max-w-md mx-auto">
                                <div className="text-xs font-black uppercase tracking-wider text-[var(--index-text-secondary)] text-left">
                                    Select Assessment Length
                                </div>
                                <div className="grid grid-cols-3 gap-2.5">
                                    {[
                                        { count: 10, label: 'Quick Diagnostic', desc: '10 Questions' },
                                        { count: 25, label: 'Standard Exam', desc: '25 Questions' },
                                        { count: 100, label: 'Full Bank', desc: 'All 100 Questions' }
                                    ].map((opt) => (
                                        <button
                                            key={opt.count}
                                            type="button"
                                            onClick={() => setQuizQuestionCount(opt.count)}
                                            className={`p-3 rounded-2xl text-left border transition-all ${
                                                quizQuestionCount === opt.count
                                                    ? 'border-[var(--index-primary-color)] bg-[var(--index-primary-color)]/10'
                                                    : 'border-[var(--index-border-color)] bg-[var(--index-hover-bg)] hover:border-[var(--index-border-color)]/80'
                                            }`}
                                        >
                                            <div className="font-black text-xs">{opt.label}</div>
                                            <div className="text-[10px] text-[var(--index-text-secondary)] font-semibold mt-0.5">{opt.desc}</div>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Info Badges */}
                            <div className="grid grid-cols-3 gap-3 max-w-md mx-auto">
                                <div className="bg-[var(--index-hover-bg)] rounded-2xl p-4">
                                    <div className="text-[9px] font-bold uppercase tracking-wider text-[var(--index-text-secondary)] mb-1">Format</div>
                                    <div className="text-xs md:text-sm font-black">Multiple Choice</div>
                                </div>
                                <div className="bg-[var(--index-hover-bg)] rounded-2xl p-4">
                                    <div className="text-[9px] font-bold uppercase tracking-wider text-[var(--index-text-secondary)] mb-1">Pass Mark</div>
                                    <div className="text-xs md:text-sm font-black">{QUIZ_PASS_MARK}%</div>
                                </div>
                                <div className="bg-[var(--index-hover-bg)] rounded-2xl p-4">
                                    <div className="text-[9px] font-bold uppercase tracking-wider text-[var(--index-text-secondary)] mb-1">Feedback</div>
                                    <div className="text-xs md:text-sm font-black">Instant Review</div>
                                </div>
                            </div>

                            <button
                                onClick={handleStartQuiz}
                                className="w-full sm:w-auto px-12 py-4 rounded-2xl font-black text-xs uppercase tracking-wider text-white transition-all hover:opacity-90 active:scale-95 shadow-md"
                                style={{ backgroundColor: category.color }}
                            >
                                Start Assessment ({quizQuestionCount} Questions)
                            </button>
                        </div>
                    ) : result ? (
                        /* Result & Review Screen */
                        showReview ? (
                            /* Review all questions */
                            <div className="space-y-6">
                                <div className="text-center space-y-1 mb-6">
                                    <h2 className="text-xl md:text-2xl font-black tracking-tight">Answer Review: {category.title}</h2>
                                    <p className="text-[var(--index-text-secondary)] text-sm font-medium">
                                        Study the authoritative explanations to master project controls principles.
                                    </p>
                                </div>

                                {sessionQuestions.map((q, qIdx) => {
                                    const userAns = selectedAnswers[qIdx] !== undefined ? Number(selectedAnswers[qIdx]) : -1;
                                    const rightAns = Number(q.correctAnswer);
                                    const isCorrect = userAns === rightAns;

                                    return (
                                        <div key={qIdx} className="bg-[var(--index-card-bg)] border border-[var(--index-border-color)] rounded-[24px] p-5 md:p-6 space-y-4">
                                            <div className="flex items-center justify-between">
                                                <span className="text-xs font-black uppercase tracking-wider text-[var(--index-text-secondary)]">
                                                    Question {qIdx + 1} (Curriculum Q{q.qNum})
                                                </span>
                                                <span
                                                    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-black uppercase tracking-wider ${
                                                        isCorrect
                                                            ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20'
                                                            : 'bg-red-500/10 text-red-600 dark:text-red-400 border border-red-500/20'
                                                    }`}
                                                >
                                                    {isCorrect ? <CheckCircle2 size={12} /> : <XCircle size={12} />}
                                                    {isCorrect ? 'Correct' : 'Incorrect'}
                                                </span>
                                            </div>

                                            <p className="font-bold text-sm md:text-base leading-relaxed">{q.question}</p>

                                            <div className="space-y-2">
                                                {q.options.map((opt, oIdx) => {
                                                    const wasSelected = userAns === oIdx;
                                                    const isRight = oIdx === rightAns;
                                                    let cls = 'bg-[var(--index-card-bg)] border-[var(--index-border-color)] text-[var(--index-text-heading)]';
                                                    let circleCls = 'bg-[var(--index-hover-bg)] text-[var(--index-text-heading)]';

                                                    if (isRight) {
                                                        cls = 'bg-emerald-500/10 border-emerald-500/40 text-emerald-700 dark:text-emerald-300 font-bold';
                                                        circleCls = 'bg-emerald-500 text-white';
                                                    } else if (wasSelected) {
                                                        cls = 'bg-red-500/10 border-red-500/40 text-red-700 dark:text-red-300 font-bold';
                                                        circleCls = 'bg-red-500 text-white';
                                                    }

                                                    return (
                                                        <div key={oIdx} className={`flex items-center gap-3 p-3 rounded-xl border ${cls}`}>
                                                            <div className={`w-7 h-7 rounded-lg flex items-center justify-center font-black text-xs shrink-0 ${circleCls}`}>
                                                                {String.fromCharCode(65 + oIdx)}
                                                            </div>
                                                            <span className="text-sm font-medium flex-1">{opt}</span>
                                                            {isRight && <CheckCircle2 size={16} className="text-emerald-500 shrink-0" />}
                                                            {!isCorrect && wasSelected && <XCircle size={16} className="text-red-500 shrink-0" />}
                                                        </div>
                                                    );
                                                })}
                                            </div>

                                            <div className="bg-[var(--index-hover-bg)] rounded-xl p-4 border border-[var(--index-border-color)]/60">
                                                <div className="text-[10px] font-black uppercase tracking-wider text-[var(--index-primary-color)] mb-1">
                                                    Official Reference
                                                </div>
                                                <p className="text-sm font-medium text-[var(--index-text-secondary)] leading-relaxed">
                                                    {q.explanation}
                                                </p>
                                            </div>
                                        </div>
                                    );
                                })}

                                <div className="flex gap-3">
                                    <button
                                        onClick={() => setShowReview(false)}
                                        className="flex-1 py-3.5 rounded-2xl font-black text-xs uppercase tracking-wider text-white bg-[var(--index-primary-color)] hover:opacity-90 transition-all"
                                    >
                                        Back to Score Summary
                                    </button>
                                </div>
                            </div>
                        ) : (
                            /* Score Summary Card */
                            <div className="bg-[var(--index-card-bg)] border border-[var(--index-border-color)] rounded-[32px] p-8 md:p-12 text-center space-y-6 shadow-sm">
                                {result.passed ? (
                                    <Trophy className="mx-auto text-amber-500 animate-bounce" size={64} />
                                ) : (
                                    <AlertCircle className="mx-auto text-red-500" size={64} />
                                )}

                                <div className="space-y-2">
                                    <h2 className="text-2xl md:text-3xl font-black tracking-tight">
                                        {result.passed ? 'Evaluation Passed!' : 'Requires Further Review'}
                                    </h2>
                                    <p className="text-5xl font-black" style={{ color: result.passed ? category.color : '#ef4444' }}>
                                        {result.score}%
                                    </p>
                                    <p className="text-xs font-bold uppercase tracking-wider text-[var(--index-text-secondary)]">
                                        {result.correctCount} of {sessionQuestions.length} Questions Correct
                                    </p>
                                    <p className="text-[var(--index-text-secondary)] text-sm md:text-base font-medium max-w-md mx-auto pt-2">
                                        {result.passed
                                            ? `Excellent work! You have demonstrated strong competency in ${category.title}.`
                                            : `You fell short of the ${QUIZ_PASS_MARK}% criterion. Review the explanations and retake the assessment to solidify your understanding.`}
                                    </p>
                                </div>

                                <div className="flex flex-col sm:flex-row gap-3 justify-center pt-4">
                                    <button
                                        onClick={() => setShowReview(true)}
                                        className="px-8 py-3.5 rounded-2xl font-black text-xs uppercase tracking-wider text-white bg-[var(--index-primary-color)] hover:opacity-90 transition-all"
                                    >
                                        Review Answers & Explanations
                                    </button>
                                    <button
                                        onClick={handleStartQuiz}
                                        className="px-8 py-3.5 rounded-2xl font-black text-xs uppercase tracking-wider bg-[var(--index-hover-bg)] text-[var(--index-text-heading)] hover:bg-[var(--index-border-color)] transition-all flex items-center justify-center gap-1.5"
                                    >
                                        <RotateCcw size={14} /> Retry Quiz
                                    </button>
                                    <Link
                                        to="/student/quiz"
                                        className="px-8 py-3.5 rounded-2xl font-black text-xs uppercase tracking-wider border border-[var(--index-border-color)] text-[var(--index-text-secondary)] hover:bg-[var(--index-hover-bg)] transition-all no-underline text-center flex items-center justify-center"
                                    >
                                        Back to Modules
                                    </Link>
                                </div>
                            </div>
                        )
                    ) : (
                        /* Active Quiz Question Screen */
                        <div className="bg-[var(--index-card-bg)] border border-[var(--index-border-color)] rounded-[32px] p-6 md:p-10 space-y-8 shadow-sm">
                            {/* Top Progress */}
                            <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <span className="text-xs font-bold text-[var(--index-text-secondary)]">
                                        Question {currentIndex + 1} of {sessionQuestions.length} ({answeredCount} Answered)
                                    </span>
                                    <span className="text-xs font-black uppercase tracking-wider" style={{ color: category.color }}>
                                        {category.title}
                                    </span>
                                </div>
                                <div className="h-2 bg-[var(--index-hover-bg)] rounded-full overflow-hidden">
                                    <div
                                        className="h-full rounded-full transition-all duration-300"
                                        style={{
                                            width: `${((currentIndex + 1) / sessionQuestions.length) * 100}%`,
                                            backgroundColor: category.color
                                        }}
                                    />
                                </div>
                            </div>

                            {/* Question Title */}
                            <div className="space-y-2">
                                <div className="text-[10px] font-black uppercase tracking-wider text-[var(--index-text-secondary)]">
                                    Curriculum Question {currentQuizQuestion.qNum}
                                </div>
                                <h2 className="text-lg md:text-2xl font-black leading-snug tracking-tight">
                                    {currentQuizQuestion.question}
                                </h2>
                            </div>

                            {/* Option Choices */}
                            <div className="grid grid-cols-1 gap-3">
                                {currentQuizQuestion.options.map((opt, oIdx) => {
                                    const isSelected = selectedAnswers[currentIndex] === oIdx;
                                    return (
                                        <button
                                            key={oIdx}
                                            onClick={() => setSelectedAnswers((prev) => ({ ...prev, [currentIndex]: oIdx }))}
                                            className={`flex items-center gap-4 p-4 rounded-2xl text-left border transition-all ${
                                                isSelected
                                                    ? 'border-transparent ring-2'
                                                    : 'bg-[var(--index-card-bg)] border-[var(--index-border-color)] hover:bg-[var(--index-hover-bg)]'
                                            }`}
                                            style={
                                                isSelected
                                                    ? {
                                                          backgroundColor: category.color + '12',
                                                          borderColor: category.color,
                                                          boxShadow: `0 0 0 2px ${category.color}`
                                                      }
                                                    : undefined
                                            }
                                        >
                                            <div
                                                className={`w-8 h-8 rounded-xl flex items-center justify-center font-black text-xs shrink-0 ${
                                                    isSelected ? 'text-white' : 'bg-[var(--index-hover-bg)] text-[var(--index-text-heading)]'
                                                }`}
                                                style={isSelected ? { backgroundColor: category.color } : undefined}
                                            >
                                                {String.fromCharCode(65 + oIdx)}
                                            </div>
                                            <span className="text-sm font-semibold flex-1 leading-relaxed">{opt}</span>
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Bottom Controls */}
                            <div className="flex items-center justify-between pt-4 border-t border-[var(--index-border-color)]/60">
                                <button
                                    onClick={() => setCurrentIndex((prev) => Math.max(0, prev - 1))}
                                    disabled={currentIndex === 0}
                                    className="px-6 py-3 rounded-xl font-black text-xs uppercase tracking-wider border border-[var(--index-border-color)] text-[var(--index-text-secondary)] disabled:opacity-30 hover:bg-[var(--index-hover-bg)] transition-all"
                                >
                                    Previous
                                </button>

                                {currentIndex < sessionQuestions.length - 1 ? (
                                    <button
                                        onClick={() => setCurrentIndex((prev) => prev + 1)}
                                        disabled={!hasSelectedCurrent}
                                        className="px-8 py-3 rounded-xl font-black text-xs uppercase tracking-wider text-white disabled:opacity-30 transition-all shadow-sm"
                                        style={{ backgroundColor: category.color }}
                                    >
                                        Next Question
                                    </button>
                                ) : (
                                    <button
                                        onClick={handleSubmitQuiz}
                                        disabled={!hasSelectedCurrent}
                                        className="px-8 py-3 rounded-xl font-black text-xs uppercase tracking-wider text-white disabled:opacity-30 transition-all shadow-sm"
                                        style={{ backgroundColor: category.color }}
                                    >
                                        Submit Assessment
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
